import React, { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Availability badge + hover tooltip. The badge lives inside `.dc-tree`,
 * which scrolls (`overflow-y: auto`) — a tooltip positioned relative to the
 * badge would get clipped by that scroll container no matter its z-index.
 * So instead of pure-CSS `:hover`, this tracks the badge's own
 * getBoundingClientRect() on hover and portals a `position: fixed` tooltip
 * straight to <body>, the same escape hatch the app's react-select dropdowns
 * already use (menuPortalTarget: document.body) for the same reason.
 */
function AvailabilityBadge({ className, label, tooltip }) {
  const badgeRef = useRef(null)
  const [coords, setCoords] = useState(null)

  const showTooltip = () => {
    if (!badgeRef.current) return
    const rect = badgeRef.current.getBoundingClientRect()
    setCoords({ top: rect.top, left: rect.left + rect.width / 2 })
  }
  const hideTooltip = () => setCoords(null)

  return (
    <>
      <span
        ref={badgeRef}
        className={className}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        tabIndex={0}
      >
        {label}
      </span>
      {coords && createPortal(
        <div className="dc-avail-tooltip" style={{ top: coords.top, left: coords.left }}>
          {tooltip}
          <span className="dc-avail-tooltip-arrow" />
        </div>,
        document.body
      )}
    </>
  )
}

/**
 * Area -> Topic -> Indicator picker. `groupedIndicators` is expected to be
 * pre-sorted using the same criteria as SearchWizard (compareTopicsWithinArea
 * from utils/topicOrdering.js), computed once in DataCenterScreen and shared.
 */
function DataCenterIndicatorTree({
  groupedIndicators,
  selectedIndicatorKeys,
  onToggle,
  selectedCountriesCount,
  indicatorAvailability,
  loadingAvailability,
}) {
  const [search, setSearch] = useState('')
  const [openAreas, setOpenAreas] = useState({})

  const flatIndicators = useMemo(() => {
    const list = []
    Object.values(groupedIndicators || {}).forEach((areaData) => {
      Object.values(areaData.topics).forEach((topicData) => {
        topicData.indicators.forEach((indicator) => {
          list.push({ ...indicator, topicLabel: topicData.meta.label })
        })
      })
    })
    return list
  }, [groupedIndicators])

  const searchActive = search.trim().length > 0

  const filteredFlat = useMemo(() => {
    if (!searchActive) return []
    const term = search.toLowerCase()
    return flatIndicators.filter((indicator) => (
      indicator.name.toLowerCase().includes(term) ||
      (indicator.topicLabel || '').toLowerCase().includes(term)
    ))
  }, [flatIndicators, search, searchActive])

  const toggleAreaOpen = (areaKey) => {
    setOpenAreas((prev) => ({ ...prev, [areaKey]: !prev[areaKey] }))
  }

  // Same "X/Y countries available" badge as the Explorer's SearchWizard,
  // fed by the same /indicators-for-countries/ endpoint. Hovering it spells
  // out which of the *selected* countries have no data for this indicator —
  // that's the actionable gap, more useful than repeating the ones that do.
  const renderAvailabilityBadge = (selectionKey) => {
    if (!selectedCountriesCount) return null
    const avail = indicatorAvailability?.[selectionKey]
    if (!avail) return null
    const nAvail = avail.available.length
    const missing = avail.unavailable || []
    const missingTitle = missing.length > 0
      ? `Not available for: ${missing.join(', ')}`
      : 'Available for all selected countries'

    if (nAvail === selectedCountriesCount) {
      return <AvailabilityBadge className="dc-avail-badge is-all" label={`${nAvail}/${selectedCountriesCount}`} tooltip={missingTitle} />
    }
    if (nAvail > 0) {
      return <AvailabilityBadge className="dc-avail-badge is-partial" label={`${nAvail}/${selectedCountriesCount}`} tooltip={missingTitle} />
    }
    return <AvailabilityBadge className="dc-avail-badge is-none" label="No data" tooltip={missingTitle} />
  }

  const renderIndicatorRow = (indicator) => {
    const isOn = selectedIndicatorKeys.includes(indicator.selectionKey)
    return (
      <div
        key={indicator.selectionKey}
        className={`dc-ind${isOn ? ' is-on' : ''}`}
        onClick={() => onToggle(indicator.selectionKey)}
      >
        <input type="checkbox" readOnly checked={isOn} />
        <div className="dc-ind-text">
          <div className="dc-ind-name">{indicator.name}</div>
          {indicator.topicLabel && <div className="dc-ind-meta">{indicator.topicLabel}</div>}
        </div>
        {renderAvailabilityBadge(indicator.selectionKey)}
      </div>
    )
  }

  return (
    <div className="dc-panel" id="dcPanelIndicators">
      <div className="dc-panel-head">
        <div className="dc-panel-head-left">
          <span className="dc-badge-step">2</span>
          <div>
            <h2 className="dc-panel-title">Indicators</h2>
            <span className="dc-panel-sub">Grouped by macro area and topic</span>
          </div>
          <span className={`dc-count${selectedIndicatorKeys.length === 0 ? ' is-empty' : ''}`}>
            {selectedIndicatorKeys.length} selected
          </span>
        </div>
      </div>
      <div className="dc-panel-body">
        <div className="dc-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input
            type="search"
            placeholder="Search by indicator or topic…"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loadingAvailability && (
          <div className="dc-inline-note">Loading country availability…</div>
        )}

        <div className="dc-tree">
          {searchActive ? (
            filteredFlat.length === 0 ? (
              <div className="dc-empty">No indicators match your search.</div>
            ) : (
              filteredFlat.map(renderIndicatorRow)
            )
          ) : (
            Object.entries(groupedIndicators || {}).map(([areaKey, areaData]) => {
              const areaIndicatorKeys = Object.values(areaData.topics).flatMap((t) => t.indicators.map((i) => i.selectionKey))
              const selectedInArea = areaIndicatorKeys.filter((key) => selectedIndicatorKeys.includes(key)).length
              const isOpen = !!openAreas[areaKey]
              return (
                <div key={areaKey} className={`dc-area${isOpen ? ' is-open' : ''}`}>
                  <div className="dc-area-head" onClick={() => toggleAreaOpen(areaKey)}>
                    <div className="dc-area-head-left">
                      <span className="dc-area-name">{areaData.meta.label}</span>
                    </div>
                    <div className="dc-area-meta">
                      <span className={`dc-pill-count${selectedInArea > 0 ? ' has-sel' : ''}`}>
                        {selectedInArea > 0 ? `${selectedInArea} selected` : `${areaIndicatorKeys.length}`}
                      </span>
                      <svg className="dc-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                  </div>
                  <div className="dc-area-body">
                    {Object.entries(areaData.topics).map(([topicKey, topicData]) => (
                      <div key={topicKey} className="dc-topic">
                        <div className="dc-topic-head">{topicData.meta.label}</div>
                        {topicData.indicators.map(renderIndicatorRow)}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default DataCenterIndicatorTree
